import React, {
  Dispatch, lazy, Suspense, useContext, useState,
} from 'react';
import { Box, Fade, IconButton, Modal, Typography, useTheme, Fab } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import {
  Close as CloseIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import {
  FileData, FileType, PreparedForm, S3Object,
} from 'API';
import ImageViewer from 'components/fileViewer/ImageViewer';
import OtherViewer from 'components/fileViewer/OtherViewer';
import { MH_ROLES } from 'services/auth';
import UserContext from 'shared/contexts/UserContext';
import FileModalSidebar from './FileModalSidebar';
import PreparedFormSidebar from './PreparedFormSidebar';

const PDFViewer = lazy(
  () => import('components/fileViewer/PDFViewer'),
);

const getFileModalStyles = (theme: Theme) => ({
  close: {
    gridColumn: '3 / 4',
    color: theme.palette.common.white,
  },
  mobileButton: {
    display: 'none',
    position: 'fixed',
    bottom: '1rem',
    right: '1rem',
    zIndex: 1000,
    [theme.breakpoints.down('lg')]: {
      display: 'block',
    },
  },
  root: {
    background: theme.palette.common.white,
    display: 'grid',
    height: '100vh',
    width: '100vw',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gridTemplateRows: 'auto minmax(0, 1fr)',
  },
  sidebar: {
    backgroundColor: theme.palette.common.white,
    borderLeft: `2px solid ${theme.palette.grey3}`,
    gridColumn: '2 / 3',
    gridRow: '2 / 3',
    height: '100%',
    padding: '1rem',
    maxWidth: '24rem',
    overflowY: 'auto',
    width: '24rem',
    [theme.breakpoints.down('lg')]: {
      border: 'none',
      maxWidth: 0,
      padding: 0,
      transition: 'max-width 0.1s ease-in',
    },
  },
  sidebarOpen: {
    [theme.breakpoints.down('lg')]: {
      borderLeft: `2px solid ${theme.palette.grey3}`,
      maxWidth: '24rem',
      padding: '1rem',
    },
    [theme.breakpoints.down('md')]: {
      borderLeft: 'none',
      maxWidth: '100vw',
      width: '100vw',
      padding: '1rem',
    },
  },
  sidebarForms: {
    padding: 0,
    '& .MuiTabs-scroller.MuiTabs-scrollable:nth-of-type(2n)': {
      marginLeft: '1rem',
    },
  },
  title: {
    color: theme.palette.common.white,
    gridColumn: '2 / 3',
    height: 'auto',
    lineHeight: 'normal',
    padding: '0.25rem',
    textAlign: 'center',
  },
  titleBar: {
    alignItems: 'center',
    background: theme.palette.primary.main,
    columnGap: '1rem',
    display: 'grid',
    gridTemplateColumns: '3rem minmax(0, 1fr) 3rem',
    gridTemplateRows: 'auto',
    gridColumn: '1 / 3',
    gridRow: '1/ 2',
  },
  viewer: {
    gridColumn: '1 / 3',
    gridRow: '2 / 3',
  },
  viewerWithSidebar: {
    gridColumn: '1 / 2',
  },
});

interface Props {
  file: FileData;
  fileTypeOptions?: Array<FileType>;
  open: boolean;
  preparedForm?: PreparedForm;
  preparedFormName?: string;
  setOpen: Dispatch<boolean>;
  withPreparedFormSidebar?: boolean;
  withSidebar?: boolean;
}

const FileModal: React.FC<Props> = ({
  file,
  fileTypeOptions,
  open,
  preparedForm,
  preparedFormName,
  setOpen,
  withPreparedFormSidebar,
  withSidebar,
}: Props) => {
  const theme = useTheme();
  const styles = getFileModalStyles(theme);
  const [userState] = useContext(UserContext);
  const { role } = userState;

  const isMH = MH_ROLES.includes(role);
  const isPDF = file.contentType === 'application/pdf';
  const isImage = file.contentType?.indexOf('image') === 0;
  const isOther = !(isPDF || isImage);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);

  const showStandardSidebar = (withSidebar && isMH);

  const hasSidebar = (showStandardSidebar || withPreparedFormSidebar);

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      aria-labelledby="file-title"
      closeAfterTransition
      // Full-screen white viewer needs no backdrop; the fixed backdrop otherwise
      // paints over the static content as a dark film.
      hideBackdrop
    >
      <Fade in={open}>

        <Box sx={styles.root} component="article">
          <Box sx={styles.titleBar}>
            <Typography sx={styles.title} id="file-title" component="h1">
              {file.fileName}
            </Typography>
            <IconButton
              onClick={() => setOpen(false)}
              disableRipple
              sx={styles.close}
              size="large">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={[styles.viewer, hasSidebar ? styles.viewerWithSidebar : {}]}
          >
            {isPDF
              && (
                <Suspense fallback={null}>
                  <PDFViewer s3Object={file.original as S3Object} />
                </Suspense>
              )}
            {isImage && <ImageViewer s3Object={file.original as S3Object} />}
            {isOther && <OtherViewer s3Object={file.original as S3Object} file={file} />}
          </Box>

          {hasSidebar && (
            <Fab
              color="primary"
              aria-label={showMobileSidebar ? 'Hide document information' : 'Show document information'}
              sx={styles.mobileButton}
              onClick={() => setShowMobileSidebar(!showMobileSidebar)}
              size="small"
            >
              {showMobileSidebar ? <CloseIcon /> : <MenuIcon />}
            </Fab>
          )}

          {showStandardSidebar && (
            <Box sx={[styles.sidebar, showMobileSidebar ? styles.sidebarOpen : {}]}>
              <FileModalSidebar
                file={file}
                fileTypeOptions={fileTypeOptions}
                setOpen={setOpen}
              />
            </Box>
          )}

          {withPreparedFormSidebar && preparedForm && preparedFormName && (
            <Box sx={[styles.sidebar, styles.sidebarForms, showMobileSidebar ? styles.sidebarOpen : {}]}>
              <PreparedFormSidebar
                preparedForm={preparedForm}
                preparedFormName={preparedFormName}
              />
            </Box>
          )}
        </Box>
      </Fade>
    </Modal>
  );
};

export default FileModal;
